import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
} from "@devhop/ui";
import { useEffect, useState } from "react";
import { usePreAddOptionCandidatesQuery } from "@/modules/cf-feed/presentation/api/queries";
import type { PreAddSessionOptionsDraft } from "@/modules/cf-feed/presentation/lib/pre-add-session-options";
import {
  formatKip,
  formatPriceInput,
  maskDiscountFixedLakInput,
  maskPriceInput,
  parseDiscountFixedLakInput,
  parsePriceInput,
} from "@/modules/cf-feed/presentation/ui/table-helpers";

type DraftOption = {
  adminOptionDefinitionId: string;
  label: string;
  posDefaultSurchargeLak: number;
  surchargeLak: string;
  isAutoAttach: boolean;
  requiresCustomerInput: boolean;
  selected: boolean;
};

type PreAddSessionOptionsDialogProps = {
  feedId: string;
  /** null = เฉพาะ ຊື່/ໝາຍເລກ */
  variantId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDraft: PreAddSessionOptionsDraft | null;
  onSave: (draft: PreAddSessionOptionsDraft | null) => void;
};

function parseOptionalPercent(raw: string): number | null | undefined {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return undefined;
  return n;
}

export function PreAddSessionOptionsDialog({
  feedId,
  variantId,
  open,
  onOpenChange,
  initialDraft,
  onSave,
}: PreAddSessionOptionsDialogProps) {
  const candidatesQuery = usePreAddOptionCandidatesQuery(
    feedId,
    variantId,
    open,
  );
  const [drafts, setDrafts] = useState<DraftOption[]>([]);
  const [discountPctDraft, setDiscountPctDraft] = useState("");
  const [discountFixedDraft, setDiscountFixedDraft] = useState("");
  const [keepForNextAdds, setKeepForNextAdds] = useState(true);

  useEffect(() => {
    if (!open) return;
    setDiscountPctDraft(
      initialDraft?.discountPercent == null
        ? ""
        : String(initialDraft.discountPercent),
    );
    setDiscountFixedDraft(
      initialDraft?.discountFixedLak == null
        ? ""
        : formatPriceInput(initialDraft.discountFixedLak),
    );
    setKeepForNextAdds(initialDraft?.keepForNextAdds ?? true);
  }, [open, initialDraft]);

  useEffect(() => {
    if (!open) return;
    const candidates = candidatesQuery.data?.items ?? [];
    const linkedById = new Map(
      (initialDraft?.items ?? []).map((row) => [
        row.adminOptionDefinitionId,
        row,
      ]),
    );

    setDrafts(
      candidates.map((c) => {
        const existing = linkedById.get(c.adminOptionDefinitionId);
        const amount = existing?.surchargeLak ?? c.posDefaultSurchargeLak;
        return {
          adminOptionDefinitionId: c.adminOptionDefinitionId,
          label: c.label,
          posDefaultSurchargeLak: c.posDefaultSurchargeLak,
          surchargeLak: formatPriceInput(amount),
          isAutoAttach: existing?.isAutoAttach ?? true,
          requiresCustomerInput:
            existing?.requiresCustomerInput ?? c.hasRequiredFields,
          selected: Boolean(existing) || c.alreadyLinked,
        };
      }),
    );
  }, [open, candidatesQuery.data?.items, initialDraft]);

  const handleSave = () => {
    const parsedPct = parseOptionalPercent(discountPctDraft);
    if (parsedPct === undefined) return;
    const parsedFixed = discountFixedDraft.trim()
      ? parseDiscountFixedLakInput(discountFixedDraft)
      : null;

    const items = drafts
      .filter((d) => d.selected)
      .map((d, index) => ({
        adminOptionDefinitionId: d.adminOptionDefinitionId,
        surchargeLak: parsePriceInput(d.surchargeLak) ?? 0,
        isAutoAttach: d.isAutoAttach,
        requiresCustomerInput: d.requiresCustomerInput,
        sortOrder: index,
      }));

    const discountPercent = parsedPct;
    const discountFixedLak = discountFixedDraft.trim()
      ? (parsedFixed ?? null)
      : null;

    if (
      items.length === 0 &&
      discountPercent == null &&
      discountFixedLak == null
    ) {
      onSave(null);
      onOpenChange(false);
      return;
    }

    onSave({
      items,
      discountPercent,
      discountFixedLak,
      keepForNextAdds,
    });
    onOpenChange(false);
  };

  const handleClear = () => {
    onSave(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ຕັ້ງ option ກ່ອນເພີ່ມ</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          ຄ່ານີ້ຈະໃຊ້ກັບເພີ່ມ / ເພີ່ມທີ່ເລືອກ / ເພີ່ມທັງໝົດ
          {variantId
            ? " · ໂຫຼດ option ຕາມສິນຄ້າທີ່ເລືອກ"
            : " · ຍັງບໍ່ເລືອກສິນຄ້າ — ສະແດງສະເພາະ ຊື່ / ໝາຍເລກ"}
        </p>

        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
          <div>
            <p className="font-medium text-sm">ເກັບໄວ້ໃຊ້ຕໍ່</p>
            <p className="text-muted-foreground text-xs">
              ເປີດ = ຄົງຄ່າຫຼັງເພີ່ມ · ປິດ = ໃຊ້ແລ້ວລ້າງຫຼັງເພີ່ມສຳເລັດ
            </p>
          </div>
          <Switch
            checked={keepForNextAdds}
            onCheckedChange={setKeepForNextAdds}
          />
        </div>

        <div className="space-y-3 rounded-md border border-border p-3">
          <div>
            <h3 className="font-medium text-sm">ສ່ວນຫຼຸດສິນຄ້າ</h3>
            <p className="text-muted-foreground text-xs">
              ວ່າງ = ໃຊ້ສ່ວນຫຼຸດລາຍການໂພສ · ຖ້າຕັ້ງແລ້ວຈະ override ລາຍການ
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cf-preadd-discount-pct">ສ່ວນຫຼຸດ (%)</Label>
              <Input
                id="cf-preadd-discount-pct"
                inputMode="decimal"
                placeholder="ເຊັ່ນ 10"
                value={discountPctDraft}
                onChange={(e) => setDiscountPctDraft(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-preadd-discount-fixed">
                ສ່ວນຫຼຸດ (LAK / ຊິ້ນ)
              </Label>
              <Input
                id="cf-preadd-discount-fixed"
                inputMode="numeric"
                className="tabular-nums"
                placeholder="5.000"
                value={discountFixedDraft}
                onChange={(e) =>
                  setDiscountFixedDraft(maskPriceInput(e.target.value))
                }
                onBlur={() =>
                  setDiscountFixedDraft(
                    maskDiscountFixedLakInput(discountFixedDraft),
                  )
                }
              />
            </div>
          </div>
        </div>

        {candidatesQuery.isLoading ? (
          <p className="text-sm">ກຳລັງໂຫຼດ...</p>
        ) : drafts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ພົບ option — ຕ້ອງມີ ຊື່ (NAME_PRINT) ຫຼື ໝາຍເລກ (NUMBER_PRINT)
          </p>
        ) : (
          <ul className="space-y-3">
            {drafts.map((d) => (
              <li
                key={d.adminOptionDefinitionId}
                className="rounded-md border border-border p-3"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{d.label}</p>
                    <p className="text-muted-foreground text-xs">
                      ລາຄາເລີ່ມຕົ້ນ: {formatKip(d.posDefaultSurchargeLak)}
                    </p>
                  </div>
                  <Switch
                    checked={d.selected}
                    onCheckedChange={(checked) =>
                      setDrafts((prev) =>
                        prev.map((row) =>
                          row.adminOptionDefinitionId ===
                          d.adminOptionDefinitionId
                            ? { ...row, selected: checked }
                            : row,
                        ),
                      )
                    }
                  />
                </div>
                {d.selected ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label
                        htmlFor={`preadd-price-${d.adminOptionDefinitionId}`}
                        className="mb-1 block text-xs"
                      >
                        ລາຄາ CF (0 = ຟຣີ)
                      </Label>
                      <Input
                        id={`preadd-price-${d.adminOptionDefinitionId}`}
                        inputMode="numeric"
                        className="tabular-nums"
                        value={d.surchargeLak}
                        onChange={(e) =>
                          setDrafts((prev) =>
                            prev.map((row) =>
                              row.adminOptionDefinitionId ===
                              d.adminOptionDefinitionId
                                ? {
                                    ...row,
                                    surchargeLak: maskPriceInput(
                                      e.target.value,
                                    ),
                                  }
                                : row,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="flex flex-col justify-end gap-2">
                      <Label
                        htmlFor={`preadd-auto-${d.adminOptionDefinitionId}`}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Switch
                          id={`preadd-auto-${d.adminOptionDefinitionId}`}
                          checked={d.isAutoAttach}
                          onCheckedChange={(checked) =>
                            setDrafts((prev) =>
                              prev.map((row) =>
                                row.adminOptionDefinitionId ===
                                d.adminOptionDefinitionId
                                  ? { ...row, isAutoAttach: checked }
                                  : row,
                              ),
                            )
                          }
                        />
                        ຕິດອໍເດີອັດຕະໂນມັດ
                      </Label>
                      <Label
                        htmlFor={`preadd-input-${d.adminOptionDefinitionId}`}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Switch
                          id={`preadd-input-${d.adminOptionDefinitionId}`}
                          checked={d.requiresCustomerInput}
                          onCheckedChange={(checked) =>
                            setDrafts((prev) =>
                              prev.map((row) =>
                                row.adminOptionDefinitionId ===
                                d.adminOptionDefinitionId
                                  ? {
                                      ...row,
                                      requiresCustomerInput: checked,
                                    }
                                  : row,
                              ),
                            )
                          }
                        />
                        ຕ້ອງໃຫ້ລູກຄ້າກອກ
                      </Label>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={handleClear}>
            ລ້າງຄ່າ
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="button" onClick={handleSave}>
              ໃຊ້ຄ່ານີ້
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
